import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IconWifi,
  IconDownload,
  IconUpload,
  IconTv,
  IconRocket,
  IconUsers,
  IconChevronLeft,
  IconChevronRight,
} from './PlanIcons';
import { buildWhatsAppPlanUrl } from '../utils/whatsapp';
import { fetchDadosSite } from '../utils/dadosSite';
import './PlanosAvulsos.css';

// Valores padrão exibidos enquanto o dados-site.json carrega (ou caso a busca falhe).
export const PLANOS_AVULSOS = [
  { numero: '800', unidade: 'Mega', velocidade: '800 Mega', precoInteiro: '99', precoCentavos: '90', idealPara: 'Home office e streaming' },
  { numero: '900', unidade: 'Mega', velocidade: '900 Mega', precoInteiro: '119', precoCentavos: '90', destaque: true, idealPara: 'Várias pessoas conectadas' },
  { numero: '1', unidade: 'Giga', velocidade: '1 Giga', precoInteiro: '129', precoCentavos: '90', idealPara: 'Gamers e criadores de conteúdo' },
];

const MOBILE_HIGHLIGHTS = [
  { label: 'Streaming em HD/4K', Icon: IconTv },
  { label: 'Jogos online', Icon: IconRocket },
  { label: 'Vários dispositivos', Icon: IconUsers },
  { label: 'Wi-Fi incluso', Icon: IconWifi },
];

const IconHome = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 11.5 12 4l9 7.5" />
    <path d="M5 10.5V20h14v-9.5" />
  </svg>
);

function PlanosAvulsos({ showViewAllLink = false, mobileShowcase = false }) {
  const [currentMobilePlan, setCurrentMobilePlan] = useState(0);
  const [planos, setPlanos] = useState(PLANOS_AVULSOS);

  useEffect(() => {
    let cancelado = false;

    fetchDadosSite().then((dados) => {
      if (!cancelado && Array.isArray(dados?.planosAvulsos) && dados.planosAvulsos.length > 0) {
        setPlanos(dados.planosAvulsos);
      }
    });

    return () => {
      cancelado = true;
    };
  }, []);

  const goToPrevPlan = () => {
    setCurrentMobilePlan((prev) => (prev - 1 + planos.length) % planos.length);
  };

  const goToNextPlan = () => {
    setCurrentMobilePlan((prev) => (prev + 1) % planos.length);
  };

  return (
    <>
      {mobileShowcase && (
        <div className="avulsos-showcase" aria-label="Planos em destaque para mobile">
          <div className="avulsos-showcase__viewport">
            <button
              type="button"
              className="avulsos-showcase__arrow avulsos-showcase__arrow--prev"
              onClick={goToPrevPlan}
              aria-label="Ver plano anterior"
            >
              <IconChevronLeft width={18} height={18} />
            </button>

            <div className="avulsos-showcase__mask">
              <div
                className="avulsos-showcase__track"
                style={{ transform: `translateX(-${currentMobilePlan * 100}%)` }}
              >
                {planos.map((plano) => (
                  <article key={`mobile-${plano.numero}`} className="avulsos-showcase__card">
                    <div className="avulsos-showcase__shell">
                      <div className="avulsos-showcase__header">
                        <div>
                          <span className="avulsos-showcase__kicker">Internet</span>
                          <div className="avulsos-showcase__headline">
                            <span className="avulsos-showcase__number">{plano.numero}</span>
                            <span className="avulsos-showcase__unit">{plano.unidade}</span>
                          </div>
                        </div>

                        <div className="avulsos-showcase__ideal">
                          <span className="avulsos-showcase__ideal-icon"><IconHome /></span>
                          <span>Ideal para {plano.idealPara}</span>
                        </div>
                      </div>

                      <div className="avulsos-showcase__highlights">
                        {MOBILE_HIGHLIGHTS.map(({ label, Icon }) => (
                          <div key={`${plano.numero}-${label}`} className="avulsos-showcase__highlight">
                            <span className="avulsos-showcase__highlight-icon"><Icon width={18} height={18} /></span>
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>

                      <div className="avulsos-showcase__price-block">
                        <span className="avulsos-showcase__price-value">R$ {plano.precoInteiro},{plano.precoCentavos}</span>
                        <span className="avulsos-showcase__price-period">por mês</span>
                      </div>

                      {plano.nota && <p className="avulsos-showcase__note">{plano.nota}</p>}

                      <a
                        href={buildWhatsAppPlanUrl(plano.velocidade)}
                        target="_blank"
                        rel="noreferrer"
                        className="avulsos-showcase__cta"
                      >
                        Contratar agora
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <button
              type="button"
              className="avulsos-showcase__arrow avulsos-showcase__arrow--next"
              onClick={goToNextPlan}
              aria-label="Ver próximo plano"
            >
              <IconChevronRight width={18} height={18} />
            </button>
          </div>

          <div className="avulsos-showcase__controls" aria-label="Controles do carrossel de planos">
            <div className="avulsos-showcase__dots" aria-label="Selecionar plano em destaque">
              {planos.map((plano, index) => (
                <button
                  key={`dot-${plano.numero}`}
                  type="button"
                  className={`avulsos-showcase__dot ${index === currentMobilePlan ? 'avulsos-showcase__dot--active' : ''}`}
                  onClick={() => setCurrentMobilePlan(index)}
                  aria-label={`Ver plano ${plano.velocidade}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={`avulsos-grid ${mobileShowcase ? 'avulsos-grid--with-showcase' : ''}`}>
        {planos.map((plano) => (
          <div className={`avulso-card ${plano.destaque ? 'avulso-card--destaque' : ''}`} key={plano.numero}>
            {plano.destaque && <span className="avulso-card__ribbon">Melhor Oferta!</span>}
            <div className="avulso-card__header">
              <span className="avulso-card__numero">{plano.numero}</span>
              <span className="avulso-card__unidade-pill">{plano.unidade}</span>
            </div>
            <div className="avulso-card__body">
              {plano.idealPara && <p className="avulso-card__ideal">Ideal para {plano.idealPara}</p>}
              <ul className="avulso-card__specs">
                <li><IconWifi width={18} height={18} />Wi-Fi Incluso</li>
                <li><IconDownload width={18} height={18} />{plano.velocidade} de Download</li>
                <li><IconUpload width={18} height={18} />{plano.velocidade} de Upload</li>
              </ul>
              <div className="avulso-card__price">
                <span className="avulso-card__price-value">R$ {plano.precoInteiro},{plano.precoCentavos}</span>
                <span className="avulso-card__price-period">/mês</span>
              </div>
              {plano.nota && <p className="avulso-card__note">{plano.nota}</p>}
              <a
                href={buildWhatsAppPlanUrl(plano.velocidade)}
                target="_blank"
                rel="noreferrer"
                className="avulso-card__cta"
              >
                Contrate Já
              </a>
            </div>
          </div>
        ))}
      </div>

      {showViewAllLink && (
        <div className={`avulsos-grid__footer ${mobileShowcase ? 'avulsos-grid__footer--with-showcase' : ''}`}>
          <Link to="/planos" className="avulsos-grid__link">Ver todos os planos e pacotes completos →</Link>
        </div>
      )}
    </>
  );
}

export default PlanosAvulsos;
